import React from 'react';

const WorkerCard = ({ worker }) => {
  if (!worker) {
    return null;
  }

  const getColorFromName = (nombre = '') => {
    let hash = 0;
    let hasNumbers = false;
    let numberSum = 0;
    
    for (let i = 0; i < nombre.length; i++) {
      const char = nombre[i];
      const code = nombre.charCodeAt(i);
      
      if (char >= '0' && char <= '9') {
        hasNumbers = true;
        const numValue = parseInt(char, 10);
        numberSum += numValue * 3;
        hash += numValue * 37;
      }
      
      hash = code + ((hash << 5) - hash);
    }
    
    if (hasNumbers) {
      hash += numberSum * 17;
    }
    
    const h = Math.abs(hash) % 360;
    const s = hasNumbers 
      ? 65 + (Math.abs(hash) % 20)
      : 60 + (Math.abs(hash) % 20);
    const l = hasNumbers
      ? 40 + (Math.abs(hash >> 2) % 20)
      : 45 + (Math.abs(hash) % 10);

    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  // Destructuración completa con valores por defecto (incluyendo nuevos campos)
  const {
    nombre = 'Sin nombre',
    operaciones = 0,
    cantidad = 0,
    importe = 0,
    pvp = 0,
    porcentajeOperaciones = 0,
    porcentajeImporte = 0,
    porcentajePVP = 0,
    ticketMedioPVP = 0,
    ticketMedioCantidad = 0,
    ticketMedioImporte = 0,
    porcentajePVPTotal = 0,
    // Nuevos campos
    pvpLibre = 0,
    cantidadLibre = 0,
    porcentajePVPLibre = 0,
    porcentajeCantidadLibre = 0
  } = worker;

  const avatarColor = getColorFromName(nombre);
  const displayChar = nombre.charAt(0);

  // Formatear moneda
  const formatCurrency = (value) => {
    return Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Formatear número
  const formatNumber = (value) => {
    return Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Formatear porcentaje
  const formatPercent = (value) => {
    const num = Number(value);
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}%`;
  };

  return (
    <div className="worker-card">
      {/* Header con avatar e info principal */}
      <div className="worker-header">
        <div className="worker-avatar-container">
          <svg 
            className="worker-silhouette"
            viewBox="0 0 100 100" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M50 55c13.8 0 25-11.2 25-25S63.8 5 50 5 25 16.2 25 30s11.2 25 25 25zm0 5c-16.7 0-50 8.3-50 25v10h100v-10c0-16.7-33.3-25-50-25z"
              fill={avatarColor}
            />
          </svg>
          <span className="worker-avatar-letter">{displayChar}</span>
        </div>
        
        <div className="worker-main-info">
          <h3 className="worker-name">{nombre}</h3>
          <div className="worker-stats-row">
            <span className="worker-stat">
              <strong>{formatNumber(operaciones)}</strong> ops
              <span className={`stat-percent ${porcentajeOperaciones >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(porcentajeOperaciones)}
              </span>
            </span>
            <span className="worker-stat-divider">•</span>
            <span className="worker-stat">
              <strong>{formatNumber(cantidad)}</strong> uds
            </span>
          </div>
        </div>
      </div>
      
      {/* Métricas financieras principales */}
      <div className="worker-financials">
        <div className="financial-block">
          <span className="financial-label">PVP</span>
          <span className="financial-value">{formatCurrency(pvp)} €</span>
          <span className={`financial-percent ${porcentajePVP >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(porcentajePVP)}
          </span>
        </div>

        <div className="financial-block">
          <span className="financial-label">Importe</span>
          <span className="financial-value">{formatCurrency(importe)} €</span>
          <span className={`financial-percent ${porcentajeImporte >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(porcentajeImporte)}
          </span>
        </div>
      </div>

      {/* NUEVA SECCIÓN: Métricas Libres */}
      <div className="worker-libre-section">
        <h4 className="section-title">Libre</h4>
        <div className="libre-metrics">
          <div className="libre-block">
            <span className="libre-label">PVP Libre</span>
            <span className="financial-value">{formatCurrency(pvpLibre)} €</span>
            <span className={`financial-percent ${porcentajePVPLibre >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(porcentajePVPLibre)}
            </span>
          </div>
          <div className="libre-block">
            <span className="libre-label">Unid. Libre</span>
            <span className="financial-value">{formatNumber(cantidadLibre)}</span>
            <span className={`financial-percent ${porcentajeCantidadLibre >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(porcentajeCantidadLibre)}
            </span>
          </div>
        </div>
      </div>

      {/* Tickets medios - CAMBIO: "Cant. Media" → "Unid. Media" */}
      <div className="worker-tickets">
        <div className="ticket-item">
          <span className="ticket-label">Ticket Medio</span>
          <span className="ticket-value">{formatCurrency(ticketMedioImporte)} €</span>
        </div>
        <div className="ticket-divider"></div>
        <div className="ticket-item">
          <span className="ticket-label">PVP Medio</span>
          <span className="ticket-value">{formatCurrency(ticketMedioPVP)} €</span>
        </div>
        <div className="ticket-divider"></div>
        <div className="ticket-item">
          <span className="ticket-label">Unid. Media</span>
          <span className="ticket-value">{formatCurrency(ticketMedioCantidad)}</span>
        </div>
      </div>

      {/* Barra de progreso de operaciones */}
      <div className="worker-progress">
        <div className="progress-header">
          <span className="progress-label">Contribución Operaciones</span>
          <span className="progress-value">{porcentajePVPTotal.toFixed(1)}%</span>
        </div>
        <div className="progress-bar-bg">
          <div 
            className="progress-bar-fill"
            style={{ width: `${Math.min(Math.abs(porcentajePVPTotal), 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default WorkerCard;